import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, race, switchMap, take, throwError } from 'rxjs';
import type { Observable } from 'rxjs';

import { SkillsImportActions } from '@core/tellent-skills-data';
import type {
  SkillsImport,
  SkillsImportCreateParams,
  SkillsImportResult,
} from '@core/tellent-skills-types';

@Injectable({ providedIn: 'root' })
export class SkillsImportDataFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public applyImport(importId: number, result: SkillsImportResult): Observable<SkillsImport> {
    this.store.dispatch(SkillsImportActions.applyImport({ payload: { importId, result } }));

    return race(
      this.actions$.pipe(
        ofType(SkillsImportActions.applyImportSuccess),
        map(({ payload }) => payload.data),
      ),
      this.actions$.pipe(
        ofType(SkillsImportActions.applyImportFailed),
        switchMap(({ payload }) => throwError(() => payload)),
      ),
    ).pipe(take(1));
  }

  public submitImport(importId: number, userPrompt: string | null): Observable<void> {
    this.store.dispatch(SkillsImportActions.submitImport({ payload: { importId, userPrompt } }));

    return race(
      this.actions$.pipe(
        ofType(SkillsImportActions.submitImportSuccess),
        map(() => undefined),
      ),
      this.actions$.pipe(
        ofType(SkillsImportActions.submitImportFailed),
        switchMap(({ payload }) => throwError(() => payload)),
      ),
    ).pipe(take(1));
  }

  public createImport(params: SkillsImportCreateParams): Observable<SkillsImport> {
    this.store.dispatch(SkillsImportActions.createImport({ payload: params }));

    return race(
      this.actions$.pipe(
        ofType(SkillsImportActions.createImportSuccess),
        map(({ payload }) => payload.data),
      ),
      this.actions$.pipe(
        ofType(SkillsImportActions.createImportFailed),
        switchMap(({ payload }) => throwError(() => payload)),
      ),
    ).pipe(take(1));
  }

  public fetchImport(id: number): Observable<SkillsImport> {
    this.store.dispatch(SkillsImportActions.fetchImport({ payload: { id } }));

    return race(
      this.actions$.pipe(
        ofType(SkillsImportActions.fetchImportSuccess),
        map(({ payload }) => payload.data),
      ),
      this.actions$.pipe(
        ofType(SkillsImportActions.fetchImportFailed),
        switchMap(({ payload }) => throwError(() => payload)),
      ),
    ).pipe(take(1));
  }
}
